import React, { useState } from "react";
import {
  Box,
  Typography,
  Paper,
  List,
  ListItemButton,
  ListItemText,
  Divider,
  useMediaQuery,
  Link as MuiLink,
} from "@mui/material";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";

import PlayCircleOutlineIcon from "@mui/icons-material/PlayCircleOutline";
import RuleIcon from "@mui/icons-material/Rule";
import BlockIcon from "@mui/icons-material/Block";
import ArticleIcon from "@mui/icons-material/Article";
import EmojiObjectsIcon from "@mui/icons-material/EmojiObjects";
import ReviewsIcon from "@mui/icons-material/Reviews";

const HOW_TO_TOPICS = [
  {
    id: "getting-started",
    title: "Getting started",
    body: [
      "1. Choose a page to work on.",
      "2. Read the page image carefully from top to bottom.",
      "3. Type what you see into the transcription box.",
      "",
      "General flow:",
      "• Transcribe: one volunteer types the page.",
      "• Review: another volunteer checks for accuracy.",
      "• Complete: once accepted, the transcription is ready for use in search and research.",
    ].join("\n"),
  },
  {
    id: "basic-rules",
    title: "Basic rules",
    body: [
      "Text order:",
      "• Transcribe text in the order it appears on the page.",
      "• If you are unsure, transcribe it in the order that makes sense if read aloud.",
      "",
      "Spelling, grammar, and punctuation:",
      "• Preserve original spelling, grammar, and punctuation.",
      "• Do not modernize or paraphrase.",

      "",
      "Line and page breaks:",
      "• Preserve line breaks unless a single word is split.",
      "• If a word is split across lines or pages, type the whole word where it begins.",
      "• If a page is blank, type 'Nothing to transcribe' and save.",
    ].join("\n"),
  },
  {
    id: "things-to-avoid",
    title: "Things to avoid",
    body: [
      "Avoid these while transcribing:",
      "",
      "• Do not add notes or comments into the transcription text.",
      "• Do not expand abbreviations, type exactly what you see.",
      "• Do not try to recreate layout (columns, fancy spacing, etc.).",
      "• Do not describe images or decorations in the transcription box.",
      "",
    ].join("\n"),
  },
  {
    id: "print-and-images",
    title: "Printed text and images",
    body: [
      "Printed or typed text:",
      "• Still needs to be transcribed so it becomes searchable.",
      "• 'Transcribe with OCR' may help, but you should always correct the result.",
      "",
      "Catalog numbers, headings, and letterhead:",
      "• Transcribe page numbers, catalog numbers, and letterheads, they often contain names, places, and dates.",
      "",
      "Images only:",
      "• If a page has only images and no text, type 'Nothing to transcribe'.",
    ].join("\n"),
  },
  {
    id: "unusual-text",
    title: "Unusual text and symbols",
    body: [
      "Illegible or unclear text:",
      "• If you truly cannot read a word, use [?].",
      "• If you can read part of a word, type those letters and use question marks for the rest, e.g. M[????].",
    ].join("\n"),
  },
  {
    id: "review",
    title: "How to review",
    body: [
      "Review is the final quality check before a page is marked complete.",
      "",
      "When reviewing:",
      "• Read the entire transcription while looking at the page.",
      "• Go line-by-line; preserved line breaks make this easier.",
      "• Check for missing words, typos, and deviations from the rules.",
      "",
      "Actions:",
      "• Accept: only if the transcription is complete and accurate.",
      "• Edit: if you need to correct or complete text. Save and submit so another reviewer can check again.",
      "",
      "You should not review pages you originally transcribed yourself.",
    ].join("\n"),
  },
];

// Small helper to pick an icon per topic
const getTopicIcon = (id) => {
  switch (id) {
    case "getting-started":
      return <PlayCircleOutlineIcon fontSize="small" sx={{ mr: 1 }} />;
    case "basic-rules":
      return <RuleIcon fontSize="small" sx={{ mr: 1 }} />;
    case "things-to-avoid":
      return <BlockIcon fontSize="small" sx={{ mr: 1 }} />;
    case "print-and-images":
      return <ArticleIcon fontSize="small" sx={{ mr: 1 }} />;
    case "unusual-text":
      return <EmojiObjectsIcon fontSize="small" sx={{ mr: 1 }} />;
    case "review":
      return <ReviewsIcon fontSize="small" sx={{ mr: 1 }} />;
    default:
      return <HelpOutlineIcon fontSize="small" sx={{ mr: 1 }} />;
  }
};

const TranscriptionInstructions = () => {
  const [activeTopicId, setActiveTopicId] = useState(HOW_TO_TOPICS[0].id);
  const isMobile = useMediaQuery("(max-width:768px)");
  const activeTopic =
    HOW_TO_TOPICS.find((t) => t.id === activeTopicId) || HOW_TO_TOPICS[0];

  return (
    <Box
      sx={{
        width: "auto",
        margin: "0 auto",
        padding: { xs: "20px", sm: "30px", md: "1px 80px" },
        backgroundColor: "#ffffff",
        borderRadius: "8px",
      }}
    >
      {/* Page heading */}
      <Typography
        variant="h5"
        sx={{
          fontWeight: 600,
          mb: 2,
          textAlign: "left",
        }}
      >
        Transcription Instructions
      </Typography>

      {/* How-To Guide section */}
      <Paper
        elevation={2}
        sx={{
          mb: 4,
          overflow: "hidden",
          borderRadius: 2,
          border: "1px solid rgba(0,0,0,0.08)",
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: isMobile ? "column" : "row",
            minHeight: { xs: "auto", sm: 320 },
          }}
        >
          {/* Topic list – left */}
          <Box
            sx={{
              width: { xs: "100%, sm:280" },
              display: "flex",
              borderRight: { sm: "1px solid rgba(0,0,0,0.08)" },
              borderBottom: { xs: "1px solid rgba(0,0,0,0.08)", sm: "none" },
              backgroundColor: "grey.50",
              justifyContent: { xs: "center", sm: "stretch" },
              overflowX: { xs: "auto", sm: "visible" },
            }}
          >
            <List
              dense
              disablePadding
              sx={{
                display: { xs: "flex", sm: "block" },
                flexDirection: { xs: "row", sm: "column" },
                gap: { xs: 1, sm: 0 },
                p: { xs: 1, sm: 0 },
                whiteSpace: "nowrap",

                width: "100%",
                minWidth: "100%",
                overflowX: { xs: "auto", sm: "visible" },
                WebkitOverflowScrolling: "touch",
              }}
            >
              {HOW_TO_TOPICS.map((topic) => {
                const selected = topic.id === activeTopicId;

                return (
                  <ListItemButton
                    key={topic.id}
                    selected={selected}
                    onClick={() => setActiveTopicId(topic.id)}
                    sx={{
                      flex: { xs: "0 0 auto", sm: "unset" },
                      px: 2,
                      py: 1.1,
                      borderRadius: { xs: 999, sm: 0 },
                      minWidth: { xs: "auto", sm: "unset" },
                      "&.Mui-selected": {
                        backgroundColor: "rgba(25,118,210,0.10)",
                        borderLeft: { sm: "4px solid" },
                        borderLeftColor: { sm: "primary.main" },
                      },
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center" }}>
                      {getTopicIcon(topic.id)}
                      <ListItemText
                        primary={topic.title}
                        primaryTypographyProps={{
                          fontWeight: selected ? 700 : 500,
                          fontSize: { xs: "0.92rem", sm: "0.98rem" },
                        }}
                      />
                    </Box>
                  </ListItemButton>
                );
              })}
            </List>
          </Box>
          {/* Topic content – right */}
          <Box sx={{ flex: 1, p: { xs: 2.5, sm: 3 } }}>
            <Box
              sx={{ display: "flex", alignItems: "center", gap: 1.2, mb: 1.8 }}
            >
              <HelpOutlineIcon color="primary" />
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 700,
                  fontSize: { xs: "1.02rem", sm: "1.15rem" },
                  lineHeight: 1.25,
                }}
              >
                {activeTopic.title}
              </Typography>
            </Box>
            {activeTopicId === "getting-started" && (
              <Box
                sx={{
                  mb: 2,
                  position: "relative",
                  paddingTop: "56.25%",
                  borderRadius: 2,
                  overflow: "hidden",
                  boxShadow: 1,
                }}
              >
                <iframe
                  src="https://www.youtube.com/embed/LIeGezuncbo"
                  title="Getting Started Walkthrough"
                  frameBorder="0"
                  allowFullScreen
                  style={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    width: "100%",
                    height: "100%",
                  }}
                />
              </Box>
            )}
            <Typography
              variant="body1"
              sx={{
                whiteSpace: "pre-line",
                lineHeight: { xs: 1.75, sm: 1.9 },
                textAlign: { xs: "left", sm: "justify" },
                fontSize: { xs: "0.96rem", sm: "1.02rem" },
                wordBreak: "break-word",
              }}
            >
              {activeTopic.body}
            </Typography>
          </Box>
        </Box>
      </Paper>

      <Divider sx={{ mb: 2 }} />
      <Typography
        variant="body2"
        color="text.secondary"
        sx={{
          lineHeight: 1.8,
          fontSize: { xs: "0.9rem", sm: "0.95rem" },
        }}
      >
        For more detailed project-specific instructions, you may also contact{" "}
        <MuiLink
          href="mailto:dh@usm.edu"
          sx={{
            color: "primary.dark",
            fontWeight: 600,
            textDecorationColor: "primary.light",
            "&:hover": {
              color: "primary.main",
              textDecoration: "underline",
            },
          }}
        >
          dh@usm.edu
        </MuiLink>{" "}
        or visit the{" "}
        <MuiLink
          href="/Chat-Thread"
          sx={{
            color: "secondary.dark",
            fontWeight: 600,
            textDecorationColor: "secondary.light",
            "&:hover": {
              color: "secondary.main",
              textDecoration: "underline",
            },
          }}
        >
          Hub discussion forum
        </MuiLink>
        .
      </Typography>
    </Box>
  );
};

export default TranscriptionInstructions;
